import {
  Factory, Recycle, Flame, Wind, Droplets, Sprout, Mountain, BatteryCharging,
  Zap, CloudFog, Waves, Leaf, Fuel, Atom, TreePine, Sun, Blocks, Shapes, Building2,
} from "lucide-react";

export const CATEGORIES = [
  "All",
  "Metals & Steel",
  "Energy & Storage",
  "Waste to Energy",
  "Air & Water",
  "Carbon Sequestration & NASA-Recommended Superfood Production",
  "Green Building",
  "Mine Restoration",
];

export const SERVICES = [
  {
    id: "steel-plants",
    category: "Metals & Steel",
    icon: Factory,
    title: "Scrap Metal & Iron Ore Steel Plants",
    tagline: "Complete steel plants built on scrap and iron ore feedstock.",
    points: ["Electric arc & induction furnace routes", "DRI / sponge iron configurations", "Turnkey concept-to-commissioning delivery"],
    flow: ["Scrap & ore preparation", "Melting (EAF / induction)", "Ladle refining", "Continuous casting", "Rolling & finishing"],
    config: ["EAF or induction melt shop", "Ladle refining station", "Continuous casting machine", "Bar / section rolling mill", "Fume extraction & bag house", "Closed-loop water treatment"],
  },
  {
    id: "metal-recycling",
    category: "Metals & Steel",
    icon: Recycle,
    title: "Metal Waste to Finished Steel",
    tagline: "Carbon steel, alloy steel and stainless steel from metal waste streams.",
    points: ["Secondary metallurgy & refining", "Continuous casting & rolling", "Product-grade quality assurance"],
    flow: ["Scrap sorting & shredding", "Melting", "Secondary refining", "Billet / slab casting", "Rolling to finished grade"],
    config: ["Shredder & sorting line", "Induction / EAF furnaces", "Refining & degassing station", "Billet caster", "Bar, wire or section mill", "QC & testing laboratory"],
  },
  {
    id: "aluminium-recycling",
    category: "Metals & Steel",
    icon: Atom,
    title: "Aluminium Scrap Recycling",
    tagline: "Ingots, extruded sections, rolled products and conductors.",
    points: ["Scrap sorting & melting furnaces", "Extrusion & rolling lines", "Electrical conductor production"],
    flow: ["Scrap sorting & decoating", "Melting", "Alloying & fluxing", "Ingot / billet casting", "Extrusion & rolling"],
    config: ["Tilting rotary furnace", "Holding & alloying furnaces", "Ingot & billet casting lines", "Extrusion presses", "Conductor rolling & drawing", "Dross recovery unit"],
  },
  {
    id: "copper-recycling",
    category: "Metals & Steel",
    icon: Zap,
    title: "Copper Recycling",
    tagline: "Rolled copper conductors and tubes from secondary copper.",
    points: ["Cathode-grade refining", "Rod breakdown & tube drawing", "High-conductivity output"],
    flow: ["Scrap grading & cleaning", "Smelting & refining", "Continuous rod casting", "Rod breakdown & drawing", "Tube forming & annealing"],
    config: ["Refining furnace", "Continuous cast rod line", "Rod breakdown mills", "Multi-wire drawing machines", "Tube drawing benches", "Annealing furnaces"],
  },
  {
    id: "ferro-alloys",
    category: "Metals & Steel",
    icon: Flame,
    title: "Ferro Alloys Smelting",
    tagline: "Smelting of iron, manganese and chrome into ferro alloys.",
    points: ["Submerged arc furnace design", "FeMn, FeSi, FeCr production", "Energy-optimized operations"],
    flow: ["Ore & reductant preparation", "Blending & charging", "Submerged arc smelting", "Tapping & casting", "Crushing, sizing & dispatch"],
    config: ["Submerged arc furnaces", "Raw material handling & batching", "Casting beds & pans", "Crushing & screening plant", "Off-gas cleaning system", "Water granulation circuit"],
  },
  {
    id: "waste-heat-power",
    category: "Energy & Storage",
    icon: Flame,
    title: "Waste Heat Recovery Power",
    tagline: "Power generation captured from industrial waste heat.",
    points: ["WHR boilers & ORC systems", "Cement, steel & furnace applications", "Captive power offset"],
    flow: ["Waste heat capture", "WHR boiler / ORC vaporization", "Turbine expansion", "Condensing & cooling", "Captive power supply"],
    config: ["AQC / SP waste heat boilers", "Steam turbine generator", "ORC skid for low-grade heat", "Cooling tower & condensate system", "DCS & grid synchronization"],
  },
  {
    id: "renewable-power",
    category: "Energy & Storage",
    icon: Sun,
    title: "Thermal, Solar CSP, Syngas & Biomass Power",
    tagline: "Firm, dispatchable renewable generation across fuel types.",
    points: ["Solar CSP with thermal storage", "Biogas & biomass power islands", "Syngas engine & turbine plants"],
    flow: ["Fuel / solar input", "Thermal conversion", "Steam or engine generation", "Storage coupling", "Grid evacuation"],
    config: ["CSP field with thermal energy storage", "Biomass boiler & steam turbine", "Syngas / biogas engine hall", "Fuel handling & preparation", "Switchyard & grid interface"],
  },
  {
    id: "energy-storage",
    category: "Energy & Storage",
    icon: BatteryCharging,
    title: "Renewable Power Storage",
    tagline: "Long-duration storage beyond lithium: thermal, air and CO2 batteries.",
    points: ["Thermal batteries", "Compressed air & liquid air storage", "CO2 battery systems"],
    flow: ["Charging from renewables", "Energy stored in medium", "Long-duration hold", "Discharge & regeneration", "Power & heat delivery"],
    config: ["Thermal battery modules", "CAES pressure vessels / caverns", "LAES liquefaction & regasification train", "CO2 dome battery skid", "Power conversion & controls"],
  },
  {
    id: "air-separation",
    category: "Air & Water",
    icon: Wind,
    title: "Air Separation Plants",
    tagline: "Pure oxygen, nitrogen and argon production.",
    points: ["Cryogenic ASU design", "Medical & industrial grade gases", "On-site gas supply schemes"],
    flow: ["Air compression", "Purification & drying", "Cryogenic distillation", "Product liquefaction & storage", "Filling & pipeline supply"],
    config: ["Main air compressor", "Molecular sieve purification unit", "Cold box & distillation columns", "LOX / LIN / LAR storage tanks", "Cylinder filling & vaporizers", "Pipeline supply system"],
  },
  {
    id: "pollution-control",
    category: "Air & Water",
    icon: CloudFog,
    title: "Fume Extraction & Air Pollution Control",
    tagline: "Bag filters and complete air pollution control systems.",
    points: ["Fume extraction & capture design", "Bag filter houses & ESPs", "Emission compliance engineering"],
    flow: ["Fume capture at source", "Ducting & spark arresting", "Bag filter / ESP separation", "Induced draft discharge", "Continuous emission monitoring"],
    config: ["Canopy & side-draft hoods", "Ductwork & spark arresters", "Pulse-jet bag house", "ID fans & stacks", "CEMS monitoring", "Dust handling & disposal"],
  },
  {
    id: "water-treatment",
    category: "Air & Water",
    icon: Droplets,
    title: "Water & Wastewater Treatment",
    tagline: "Industrial water and wastewater treatment plants.",
    points: ["ETP, STP & ZLD systems", "Recycling & reuse loops", "Process water polishing"],
    flow: ["Screening & equalization", "Physico-chemical / biological treatment", "Clarification", "Filtration & disinfection", "Reuse or compliant discharge"],
    config: ["ETP / STP treatment trains", "ASP, MBBR or MBR biology", "Clarifiers & thickeners", "UF / RO for zero liquid discharge", "Sludge dewatering", "SCADA & water quality lab"],
  },
  {
    id: "waste-to-gas",
    category: "Waste to Energy",
    icon: Fuel,
    title: "Waste to Biogas, RNG, CBG & CNG",
    tagline: "Municipal, forest and agri waste converted to renewable gas.",
    points: ["Anaerobic digestion plants", "RNG / CBG upgrading & compression", "CNG cascade & dispensing"],
    flow: ["Waste reception & segregation", "Pre-treatment & pulping", "Anaerobic digestion", "Biogas upgrading", "RNG / CBG / CNG output"],
    config: ["Reception hall & sorting lines", "Pulping & pre-treatment", "CSTR digesters", "Membrane / PSA upgrading", "Gas compression & cascades", "Digestate handling"],
  },
  {
    id: "gasification",
    category: "Waste to Energy",
    icon: Flame,
    title: "Gasification to Syngas",
    tagline: "End-of-life tyres, plastic waste and biomass to synthetic gas.",
    points: ["Fluidized-bed gasification", "Syngas cleaning & conditioning", "Power, fuel & chemical offtake"],
    flow: ["Feedstock preparation & drying", "Gasification", "Syngas cooling & cleaning", "Conditioning & compression", "Power, fuel or chemical synthesis"],
    config: ["Shredders & dryers", "Fluidized-bed gasifier", "Cyclones & scrubbers", "Gas conditioning train", "Gas engines or synthesis island", "Char & ash handling"],
  },
  {
    id: "tyre-recycling",
    category: "Waste to Energy",
    icon: Recycle,
    title: "Tyre to TDF, Carbon & Crumb Rubber",
    tagline: "End-of-life tyres to fuel, recovered carbon and moulded products.",
    points: ["Tyre derived fuel (TDF)", "Crumb rubber & moulded goods", "Recovered carbon black"],
    flow: ["Whole tyre reception", "Shredding & debeading", "Steel & fibre separation", "Crumbing / pyrolysis", "TDF, carbon & rubber products"],
    config: ["Tyre shredders & debeaders", "Granulators & cracker mills", "Pyrolysis reactors", "Moulding presses", "Carbon pelletizing line", "Steel & fibre recovery"],
  },
  {
    id: "organic-fertilizer",
    category: "Carbon Sequestration & NASA-Recommended Superfood Production",
    icon: Leaf,
    title: "Organic Fertilizer & Biogenic Carbon",
    tagline: "Liquid and solid organic fertilizer plus biogenic carbon.",
    points: ["Digestate valorization", "Liquid & solid formulations", "Biogenic carbon offtake"],
    flow: ["Digestate / organic input", "Solid-liquid separation", "Liquid polishing & solid composting", "Formulation & enrichment", "Packing & dispatch"],
    config: ["Screw presses & separators", "Liquid clarification train", "Compost vessels / windrows", "Granulators & blenders", "Bagging & bulk loading"],
  },
  {
    id: "micro-algae",
    category: "Carbon Sequestration & NASA-Recommended Superfood Production",
    icon: Sprout,
    title: "Micro Algae Cultivation",
    tagline: "Spirulina, Chlorella and Nanochloropsis at commercial scale.",
    points: ["Raceway ponds & photobioreactors", "Nutraceutical-grade harvesting", "CO2 sequestration coupling"],
    flow: ["Culture preparation", "Inoculation", "Growth with CO2 supply", "Harvesting & dewatering", "Drying & packing"],
    config: ["Raceway ponds or photobioreactors", "CO2 supply & dosing", "Centrifuges & filtration", "Spray / drum dryers", "Clean packing room", "Nutrient preparation station"],
  },
  {
    id: "biomass-fuels",
    category: "Carbon Sequestration & NASA-Recommended Superfood Production",
    icon: TreePine,
    title: "Biomass to Pellets, Biochar & Bio Coal",
    tagline: "Densified and torrefied solid biofuels from biomass.",
    points: ["Bio pellets & briquettes", "Biochar & torrefied bio coal", "Industrial fuel substitution"],
    flow: ["Biomass reception", "Drying & size reduction", "Densification / torrefaction", "Cooling & screening", "Storage & dispatch"],
    config: ["Rotary / belt dryers", "Hammer mills & chippers", "Pellet presses & briquetters", "Torrefaction / carbonization reactor", "Coolers & silos"],
  },
  {
    id: "biomass-chemicals",
    category: "Carbon Sequestration & NASA-Recommended Superfood Production",
    icon: Atom,
    title: "Biomass to Methanol & Ethanol",
    tagline: "Green methanol and ethanol from biomass pathways.",
    points: ["Gasification-to-methanol route", "Fermentation ethanol plants", "Fuel & chemical grade output"],
    flow: ["Biomass preparation", "Gasification / fermentation", "Synthesis or distillation", "Purification", "Product storage & loading"],
    config: ["Gasifier & syngas cleanup", "Methanol synthesis loop", "Fermentation trains", "Distillation & dehydration columns", "Product tanks & loading"],
  },
  {
    id: "saf",
    category: "Carbon Sequestration & NASA-Recommended Superfood Production",
    icon: Fuel,
    title: "Biomass to SAF",
    tagline: "Sustainable aviation fuel from biomass feedstocks.",
    points: ["HEFA & gasification-FT routes", "ASTM D7566 pathway alignment", "Blending & certification support"],
    flow: ["Feedstock pre-treatment", "Conversion (HEFA / FT)", "Hydroprocessing", "Fractionation", "SAF blending & certification"],
    config: ["Pre-treatment & filtration units", "HEFA / Fischer-Tropsch reactors", "Hydrocracker & isomerization", "Fractionation columns", "Blending & certification lab"],
  },
  {
    id: "green-bricks-blocks",
    category: "Green Building",
    icon: Blocks,
    title: "Green Bricks & Blocks",
    tagline: "Circular bricks and blocks made from recovered industrial materials.",
    points: ["Fly ash & slag valorization", "Compressed & cured block lines", "Low-embodied-carbon masonry"],
    flow: ["Raw material dosing (fly ash / slag / lime)", "Mixing", "Pressing & moulding", "Curing", "Palletizing & dispatch"],
    config: ["Batching & dosing systems", "High-shear mixers", "Hydraulic presses & moulds", "Curing chambers", "Palletizers & handling"],
  },
  {
    id: "cast-profiles",
    category: "Green Building",
    icon: Shapes,
    title: "Engineered Cast Profiles & Sections",
    tagline: "Precision-cast green building profiles and structural sections.",
    points: ["Custom moulded profiles", "Precast beams, panels & sections", "Recycled feedstock integration"],
    flow: ["Mix design & batching", "Moulding / casting", "Vibration & compaction", "Curing & demoulding", "Finishing & quality control"],
    config: ["Precision moulds & formwork", "Casting tables", "Vibration systems", "Curing yards & chambers", "Finishing & QC line"],
  },
  {
    id: "green-cement",
    category: "Green Building",
    icon: Building2,
    title: "Green Cement",
    tagline: "Low-carbon cement produced from industrial by-products.",
    points: ["Slag & fly ash blended cements", "Reduced clinker factor", "Carbon-negative potential with biogenic carbon"],
    flow: ["Raw material reception (clinker / slag / fly ash)", "Proportioning", "Grinding & blending", "Quality control", "Packing & bulk dispatch"],
    config: ["Vertical roller / ball mill", "Blending & storage silos", "High-efficiency classifier", "Packing plant", "Bulk loading station"],
  },
  {
    id: "mine-restoration",
    category: "Mine Restoration",
    icon: Mountain,
    title: "End-of-Life Mine Restoration",
    tagline: "Mined land reborn as solar & wind power plants.",
    points: ["Land profiling & stabilization", "Solar / wind repowering design", "Hybrid renewable parks"],
    flow: ["Site assessment & surveys", "Land profiling & stabilization", "Foundations & cabling", "Solar / wind installation", "Grid connection & operation"],
    config: ["Earthworks & drainage", "PV arrays / wind turbines", "Substations & cabling", "SCADA monitoring", "Fencing & access infrastructure"],
  },
  {
    id: "bioremediation",
    category: "Mine Restoration",
    icon: Waves,
    title: "Bioremediation to Agriculture Land",
    tagline: "Restoring mine land to productive agricultural use.",
    points: ["Soil detoxification programs", "Organic matter rebuilding", "Crop-ready land certification"],
    flow: ["Soil assessment & mapping", "Amendment & detox treatment", "Organic matter rebuilding", "Monitoring & validation", "Agricultural handover"],
    config: ["Soil testing laboratory", "Amendment spreading equipment", "Irrigation systems", "Cover-crop & biomass planting", "Monitoring stations"],
  },
];
